import { parse } from 'graphql';

import { createAuthenticatedGraphQLClient } from './createAuthenticatedGraphQLClient';
import type { RuntimeEnvironment } from '../graphql/config/runtimeConfig';
import type {
  FieldOpsShellIdentityQuery,
  FieldOpsShellIdentityQueryVariables,
} from '../graphql/queries/fieldOpsShellIdentity.generated';

export type DisplayIdentity = {
  personName: string | null;
  brokerageName: string | null;
};

export type DisplayIdentityRequestOptions = {
  signal?: AbortSignal;
};

export type DisplayIdentityApi = {
  getIdentity(agentId: string, options?: DisplayIdentityRequestOptions): Promise<DisplayIdentity>;
};

type AuthenticatedClient = ReturnType<typeof createAuthenticatedGraphQLClient>;
type AuthenticatedClientFactory = (options: Parameters<typeof createAuthenticatedGraphQLClient>[0]) => AuthenticatedClient;

export type CreateDisplayIdentityApiOptions = {
  env: RuntimeEnvironment;
  fetchImpl?: Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];
  authenticatedClientFactory?: AuthenticatedClientFactory;
};

// Keep this selection set in sync with fieldOpsShellIdentity.graphql. Codegen
// emits operation types only, so this is the runtime document sent to Core.
export const FIELD_OPS_SHELL_IDENTITY_QUERY = parse(/* GraphQL */ `
  query FieldOpsShellIdentity($agentId: ID!) @api(name: core) {
    agent(id: $agentId) {
      name
      brokerage {
        name
      }
    }
  }
`);

const emptyDisplayIdentity = (): DisplayIdentity => ({ personName: null, brokerageName: null });

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isDisplayName = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const readDisplayIdentity = (result: FieldOpsShellIdentityQuery): DisplayIdentity => {
  const agent: unknown = result.agent;
  if (!isRecord(agent) || !Object.hasOwn(agent, 'name') || !Object.hasOwn(agent, 'brokerage')) {
    return emptyDisplayIdentity();
  }

  const { name, brokerage } = agent;
  if (name !== null && !isDisplayName(name)) return emptyDisplayIdentity();
  if (brokerage === null) return { personName: name, brokerageName: null };
  if (!isRecord(brokerage) || !Object.hasOwn(brokerage, 'name')) return emptyDisplayIdentity();

  const brokerageName = brokerage.name;
  if (brokerageName !== null && !isDisplayName(brokerageName)) return emptyDisplayIdentity();

  return { personName: name, brokerageName };
};

export const createDisplayIdentityApi = ({
  env,
  fetchImpl,
  authenticatedClientFactory = createAuthenticatedGraphQLClient,
}: CreateDisplayIdentityApiOptions): DisplayIdentityApi => {
  const client = authenticatedClientFactory({ env, fetchImpl });

  return {
    getIdentity: async (agentId, options = {}) => {
      try {
        const variables: FieldOpsShellIdentityQueryVariables = { agentId };
        const result = await client.request<FieldOpsShellIdentityQuery>({
          document: FIELD_OPS_SHELL_IDENTITY_QUERY,
          variables,
          operationName: 'FieldOpsShellIdentity',
          signal: options.signal,
        });

        return readDisplayIdentity(result);
      } catch {
        // This presentation-only request intentionally exposes neither transport
        // details nor identity data. The authenticated client still clears the
        // session first for HTTP 401 and GraphQL UNAUTHENTICATED responses.
        return emptyDisplayIdentity();
      }
    },
  };
};
